import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * EmptyState — calm, generous, never apologetic. Used when a list is
 * empty, a search returns nothing, or a feature isn't activated yet.
 * Supports an optional primary action button slot.
 */
export function EmptyState({
  Icon,
  title,
  description,
  action,
  tone = "neutral",
  className,
}: {
  Icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  /** neutral=정보 없음 · success=이상 없음 · caution=가려졌거나 확인이 필요함.
   *  ⚠️ 안전 화면에서 색은 곧 의미다 — 「필터가 가려 0건」을 success(초록)로 보여주면
   *  경보가 있는데도 「이상 없음」이라고 말하는 화면이 된다. */
  tone?: "neutral" | "success" | "caution";
  className?: string;
}) {
  const ringClass =
    tone === "success"
      ? "bg-[color-mix(in_oklab,var(--success)_18%,transparent)] text-[var(--success)]"
      : tone === "caution"
        ? "bg-[color-mix(in_oklab,var(--caution)_18%,transparent)] text-[var(--caution)]"
        : "bg-[var(--bg-subtle)] text-[var(--fg-muted)]";

  return (
    <div className={cn("py-14 px-6 grid place-items-center text-center", className)}>
      <div className={cn("h-14 w-14 grid place-items-center rounded-full mb-4", ringClass)}>
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div className="text-[16px] font-semibold tracking-[-0.005em] text-[var(--fg)]">
        {title}
      </div>
      {description && (
        <p className="mt-1.5 max-w-[420px] text-[13.5px] text-[var(--fg-subtle)] leading-snug">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
